/**
 * MODIFIED FROM SHADCN ORIGINAL
 *
 * This component has been customized from the original shadcn/ui version.
 * Modifications: Custom empty state component
 *
 * Original: https://ui.shadcn.com/
 */

import { cn } from '@ophelos/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

function Empty({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty"
      className={cn(
        'flex min-w-0 flex-1 flex-col items-center justify-center gap-6 rounded-lg border-dashed p-6 text-center text-balance md:p-12',
        className
      )}
      {...props}
    />
  );
}

function EmptyHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="empty-header" className={cn('flex max-w-sm flex-col items-center gap-2 text-center', className)} {...props} />;
}

const emptyMediaOuterVariants = cva('p-3.5 2xl:p-4.25 border rounded-[2rem] 2xl:rounded-[2.75rem]', {
  variants: {
    variant: {
      default: 'bg-accent/25 border-ring/8',
      error: 'bg-destructive/2 border-destructive/8',
      subtle: 'bg-muted/25 border-border-light/75',
    },
  },
  defaultVariants: { variant: 'default' },
});

const emptyMediaMiddleVariants = cva('p-3.5 2xl:p-4.25 border rounded-[1.5rem] 2xl:rounded-[1.75rem] shadow-lg', {
  variants: {
    variant: {
      default: 'bg-accent/60 border-ring/15 shadow-ring/10 dark:bg-accent/50',
      subtle: 'bg-muted/50 border-border/75 shadow-lg/5',
      error:
        'bg-destructive/5 border-destructive/10 shadow-destructive/10 dark:bg-destructive/5 dark:border-destructive/10 dark:shadow-destructive/10',
    },
  },
  defaultVariants: { variant: 'default' },
});

const emptyMediaInnerVariants = cva('p-2.5 2xl:p-3.75 border rounded-xl shadow-lg', {
  variants: {
    variant: {
      default: 'bg-card/80 border-ring/35 shadow-ring/15 dark:bg-card/25 dark:border-ring/50 dark:shadow-ring/15',
      subtle: 'bg-card/80 border-border-heavy/80 shadow-lg/10',
      error: 'bg-card/80 border-destructive/25 shadow-destructive/15 dark:bg-card/25 dark:border-destructive/20 dark:shadow-destructive/15',
    },
  },
  defaultVariants: { variant: 'default' },
});

const emptyMediaIconVariants = cva(
  "flex shrink-0 items-center justify-center rounded-lg [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 2xl:[&_svg:not([class*='size-'])]:size-5",
  {
    variants: {
      variant: {
        default: 'text-accent-foreground',
        subtle: 'text-muted-foreground',
        error: 'text-destructive-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

function EmptyMedia({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof emptyMediaIconVariants>) {
  return (
    <div className={cn(emptyMediaOuterVariants({ variant }))}>
      <div className={cn(emptyMediaMiddleVariants({ variant }))}>
        <div className={cn(emptyMediaInnerVariants({ variant }))}>
          <div data-slot="empty-icon" data-variant={variant} className={cn(emptyMediaIconVariants({ variant, className }))} {...props} />
        </div>
      </div>
    </div>
  );
}

function EmptyTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="empty-title" className={cn('text-lg font-medium tracking-tight', className)} {...props} />;
}

function EmptyDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <div
      data-slot="empty-description"
      className={cn('text-muted-foreground [&>a:hover]:text-primary text-sm/relaxed [&>a]:underline [&>a]:underline-offset-4', className)}
      {...props}
    />
  );
}

function EmptyContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-content"
      className={cn('flex w-full max-w-sm min-w-0 flex-col items-center gap-4 text-sm text-balance', className)}
      {...props}
    />
  );
}

export { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle };
